import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link as ChakraLink,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { z } from "zod";
import { forgotPasswordSchema } from "./schemas";
import { authClient } from "../../lib/auth-client";

type ForgotPasswordFormInputs = z.infer<typeof forgotPasswordSchema>;

export const ForgotPasswordPage: React.FC = () => {
  const toast = useToast();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormInputs>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormInputs) => {
    const { error } = await authClient.requestPasswordReset({
      email: data.email,
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Password reset email sent.",
        description:
          "Please check your email for a link to reset your password.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box maxW="md" mx="auto" mt={10}>
      <Box textAlign="center" mb={8}>
        <ChakraLink as={RouterLink} to="/" _hover={{ textDecoration: "none" }}>
          <Heading size="lg" color="teal.500" mb={2}>
            Dokahub
          </Heading>
        </ChakraLink>
      </Box>
      <Heading textAlign="center" mb={6}>
        Forgot Password
      </Heading>
      <Box as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isInvalid={!!errors.email}>
            <FormLabel htmlFor="email">Email</FormLabel>
            <Input id="email" type="email" {...register("email")} />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>
          <Button type="submit" isLoading={isSubmitting} colorScheme="teal">
            Send Reset Link
          </Button>
        </Stack>
      </Box>
      <Text mt={4} textAlign="center">
        Remember your password?{" "}
        <ChakraLink as={RouterLink} to="/login" color="teal.500">
          Login
        </ChakraLink>
      </Text>
    </Box>
  );
};
